import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, Sparkles, Heart, Star, Instagram } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import ProductCard from "@/components/ProductCard";
import StarRating from "@/components/StarRating";

const WHY = [
    { icon: Sparkles, title: "Premium Ceramic", text: "Smooth styling plates for effortless, salon-inspired results." },
    { icon: Zap, title: "Fast Heating", text: "Ready in moments so you can style and go, every single day." },
    { icon: Heart, title: "For Every Hair Type", text: "Thoughtfully designed to suit different hair types with ease." },
    { icon: ShieldCheck, title: "1 Year Warranty", text: "Backed against manufacturing defects for peace of mind." },
];

const AMBIANCE = [
    "https://images.unsplash.com/photo-1716504628084-97224213ca6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxiZWF1dGlmdWwlMjBpbmRpYW4lMjB3b21hbiUyMGhhaXJ8ZW58MHx8fHwxNzg2NzI4NDQ0fDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1615292026763-3af006e59a67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxiZWF1dGlmdWwlMjBpbmRpYW4lMjB3b21hbiUyMGhhaXJ8ZW58MHx8fHwxNzg2NzI4NDQ0fDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1643168343279-3f93c2e592ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwYmVhdXR5JTIwY29zbWV0aWNzJTIwcHJvZHVjdHMlMjBmbGF0bGF5fGVufDB8fHx8MTc4NjcyODQ0NHww&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1762651356493-c492ca111a07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwYmVhdXR5JTIwY29zbWV0aWNzJTIwcHJvZHVjdHMlMjBmbGF0bGF5fGVufDB8fHx8MTc4NjcyODQ0NHww&ixlib=rb-4.1.0&q=85",
];

function Section({ eyebrow, title, children, cta }) {
    return (
        <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16 md:py-24">
            <div className="flex items-end justify-between mb-10">
                <div>
                    {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
                    <h2 className="font-serif text-3xl md:text-4xl tracking-tight">{title}</h2>
                </div>
                {cta}
            </div>
            {children}
        </section>
    );
}

export default function Home() {
    const { settings } = useSettings();
    const home = settings.homepage || {};
    const [featured, setFeatured] = useState([]);
    const [best, setBest] = useState([]);
    const [fresh, setFresh] = useState([]);
    const [cats, setCats] = useState([]);
    const [videos, setVideos] = useState([]);

    useEffect(() => {
        api.get("/products?featured=true&limit=4").then(({ data }) => setFeatured(data.products));
        api.get("/products?best_seller=true&limit=4").then(({ data }) => setBest(data.products));
        api.get("/products?new_arrival=true&sort=newest&limit=4").then(({ data }) => setFresh(data.products));
        api.get("/categories").then(({ data }) => setCats(data.categories));
        api.get("/reviews/featured-videos").then(({ data }) => setVideos(data.videos)).catch(() => {});
    }, []);

    const hero = home.hero_image || mediaUrl("");

    return (
        <div>
            {/* HERO */}
            <section className="relative">
                <div className="relative h-[78vh] md:h-[88vh] max-h-[820px] overflow-hidden">
                    <img src={hero} alt="ELVOSH" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/15 to-transparent" />
                    <div className="absolute inset-0 flex items-center">
                        <div className="mx-auto max-w-[1400px] w-full px-5 sm:px-8">
                            <div className="max-w-xl text-white fade-up">
                                <p className="eyebrow text-gold mb-4">ELVOSH · Elevate Your Beauty</p>
                                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                                    {home.hero_title || "Style Your Hair. Elevate Your Confidence."}
                                </h1>
                                <p className="mt-6 text-white/85 text-base md:text-lg max-w-md leading-relaxed">
                                    {home.hero_subtitle || "Premium hair styling tools, thoughtfully designed for effortless everyday beauty."}
                                </p>
                                <div className="mt-9 flex flex-wrap gap-4">
                                    <Link to="/shop" data-testid="hero-shop-now" className="bg-gold text-white px-8 py-3.5 text-sm uppercase tracking-[0.18em] hover:bg-gold-hover transition-colors">Shop Now</Link>
                                    <Link to="/shop" data-testid="hero-explore" className="border border-white/70 text-white px-8 py-3.5 text-sm uppercase tracking-[0.18em] hover:bg-white hover:text-ink transition-colors">Explore</Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {home.promo_text && (
                    <div className="bg-ink text-cream text-center text-xs md:text-sm tracking-[0.18em] uppercase py-3 px-4">{home.promo_text}</div>
                )}
            </section>

            {/* FEATURED */}
            {home.show_featured !== false && featured.length > 0 && (
                <Section eyebrow="Featured" title="The ELVOSH Signature" cta={<Link to="/shop" className="hidden sm:flex items-center gap-2 text-sm uppercase tracking-[0.15em] hover:text-gold transition-colors">View all <ArrowRight className="h-4 w-4" /></Link>}>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {featured.map((p) => <ProductCard key={p.id} product={p} />)}
                    </div>
                </Section>
            )}

            {/* WHY */}
            <section className="bg-cream-soft">
                <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16 md:py-24">
                    <div className="text-center mb-12">
                        <p className="eyebrow mb-3">Why ELVOSH</p>
                        <h2 className="font-serif text-3xl md:text-4xl tracking-tight">Crafted for Everyday Confidence</h2>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {WHY.map((w) => (
                            <div key={w.title} className="text-center">
                                <div className="mx-auto h-14 w-14 rounded-full bg-white flex items-center justify-center border border-[#E8DFD5]">
                                    <w.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                                </div>
                                <h3 className="mt-5 font-medium text-lg">{w.title}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BEST SELLERS */}
            {home.show_best_sellers !== false && best.length > 0 && (
                <Section eyebrow="Loved by many" title="Best Sellers">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {best.map((p) => <ProductCard key={p.id} product={p} />)}
                    </div>
                </Section>
            )}

            {/* SHOP BY CATEGORY */}
            {home.show_categories !== false && cats.length > 0 && (
                <Section eyebrow="Explore" title="Shop by Category">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {cats.slice(0, 8).map((c, i) => (
                            <Link key={c.id} to={`/shop?category=${encodeURIComponent(c.name)}`} data-testid={`home-category-${c.slug}`} className="group relative overflow-hidden bg-ink aspect-[4/3] flex items-end p-5">
                                {c.image_url ? (
                                    <img src={mediaUrl(c.image_url)} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" />
                                ) : (
                                    <img src={AMBIANCE[i % AMBIANCE.length]} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-55 transition-transform duration-700 group-hover:scale-105" />
                                )}
                                <span className="relative text-white font-serif text-lg md:text-xl">{c.name}</span>
                            </Link>
                        ))}
                    </div>
                </Section>
            )}

            {/* NEW ARRIVALS */}
            {home.show_new_arrivals !== false && fresh.length > 0 && (
                <Section eyebrow="Just In" title="New Arrivals">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {fresh.map((p) => <ProductCard key={p.id} product={p} />)}
                    </div>
                </Section>
            )}

            {/* BEFORE & AFTER */}
            {home.show_before_after !== false && (
                <section className="bg-ink text-cream">
                    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <p className="eyebrow text-gold mb-3">The Transformation</p>
                            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">Before & After</h2>
                            <p className="mt-5 text-cream/70 leading-relaxed max-w-md">
                                See the difference premium styling makes. Smooth, sleek and effortlessly styled — the ELVOSH way.
                            </p>
                            <Link to="/shop" className="inline-flex items-center gap-2 mt-8 border border-gold text-gold px-7 py-3 text-sm uppercase tracking-[0.15em] hover:bg-gold hover:text-white transition-colors">Discover <ArrowRight className="h-4 w-4" /></Link>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="relative aspect-[3/4] overflow-hidden">
                                <img src={AMBIANCE[0]} alt="Before" className="w-full h-full object-cover" />
                                <span className="absolute top-3 left-3 bg-black/50 text-white text-[11px] px-3 py-1 tracking-wider">BEFORE</span>
                            </div>
                            <div className="relative aspect-[3/4] overflow-hidden">
                                <img src={AMBIANCE[1]} alt="After" className="w-full h-full object-cover" />
                                <span className="absolute top-3 left-3 bg-gold text-white text-[11px] px-3 py-1 tracking-wider">AFTER</span>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* VIDEO REVIEWS */}
            {home.show_video_reviews !== false && videos.length > 0 && (
                <Section eyebrow="Real Customers" title="Video Reviews">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {videos.slice(0, 3).map((v) => (
                            <div key={v.id} className="bg-white border border-[#E8DFD5]">
                                <video src={mediaUrl(v.video)} controls preload="none" playsInline className="w-full aspect-video object-cover bg-ink" />
                                <div className="p-5">
                                    <StarRating value={v.rating} />
                                    <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{v.text}</p>
                                    <p className="mt-3 text-xs uppercase tracking-wider">{v.customer_name}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* INSTAGRAM */}
            {home.show_instagram !== false && (
                <Section eyebrow="@elvosh_official" title="Follow the Glow" cta={settings.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="hidden sm:flex items-center gap-2 text-sm uppercase tracking-[0.15em] hover:text-gold transition-colors"><Instagram className="h-4 w-4" /> Follow</a>}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        {AMBIANCE.map((src, i) => (
                            <a key={i} href={settings.instagram_url || "#"} target="_blank" rel="noopener noreferrer" className="group relative overflow-hidden aspect-square">
                                <img src={src} alt="ELVOSH Instagram" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/25 transition-colors flex items-center justify-center">
                                    <Instagram className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                                </div>
                            </a>
                        ))}
                    </div>
                </Section>
            )}
        </div>
    );
}
